import React, {useRef} from 'react';
import {useControllers} from '../../../../common/helpers/react/hooks/useControllers';
import PopoverMenu from "../../common/components/popover-menu/PopoverMenu";
import {cssClasses} from "../../../../common/helpers/browser/cssClasses";
import {tAsString} from "../../../../common/helpers/react/text/tAsString";
import {ButtonSize} from "@sabre/spark-react-core/types";
import styles from './UserButton.module.css';
import {ModalButtons} from "../../../../controllers/dialog/ModalButtons";
import {isError} from "../../../../common/types/guards/isError";
import {isStatus} from "../../../../core/rest-client/aat";
import {useOnce} from "../../../../common/helpers/react/hooks/useOnce";
import {BehaviorSubject} from "rxjs";
import EmulatePccModal, {EmulatePccModalData} from "./emulate-pcc-modal/EmulatePccModal";

export default function UserButton() {
    const controllers = useControllers();
    const dialog = controllers.dialog;
    const auth = controllers.auth;

    const authInfo = useControllers().auth.getAuthInfo$().useState();

    const isSA = authInfo?.pccsStatus?.adminType === 'SA';

    const user = authInfo?.pccsStatus?.user;
    const pcc = authInfo?.pccsStatus?.pcc;
    const onBehalfOf = authInfo?.pccsStatus?.onBehalfOf;
    const onBehalfOfScCode = authInfo?.pccsStatus?.onBehalfOfScCode;

    const userId = onBehalfOf
        ? `${user}-${pcc} > ${onBehalfOfScCode}`
        : `${user}-${pcc}`;

    const runAsPccLabel = tAsString('RUN_AS_AGENCY_ADMIN');
    const runAsSaLabel = tAsString('RUN_AS_SABRE_ADMIN');
    const signOutLabel = tAsString('SIGN_OUT');
    const defaultErrorMessage = tAsString('INVALID_PCC');

    const inputRef = useRef<HTMLInputElement>(null);

    const initialData: EmulatePccModalData = {
        inputRef: inputRef
    };

    const channel = useOnce(() => {
        return new BehaviorSubject<EmulatePccModalData>(initialData)
    });

    const emulatePcc = async () => {
        if (inputRef.current instanceof HTMLInputElement) {
            const pcc = inputRef.current.value.trim();

            try {
                if (pcc) {
                    await auth.runAs(pcc);
                    await dialog.closeModal();
                } else {
                    throw new Error(defaultErrorMessage);
                }
            } catch (e) {
                if (isError(e)) {
                    const errorMessage = isStatus(e.cause)
                        ? e.cause.statusDescription
                        : e.message;

                    channel.next({errorMessage, inputRef, pcc})
                } else {
                    throw e;
                }
            }
        }
    };

    const modalConfig = {
        title: runAsPccLabel,
        children: <EmulatePccModal channel={channel}/>,
        buttons: [
            ModalButtons.CANCEL(dialog),
            {
                children: tAsString('SUBMIT'),
                className: 'qa-modal-emulate-pcc-button',
                onClick: emulatePcc
            }
        ],
        onClose: () => {
            channel.next(initialData);
        }
    }

    const options = [
        [
            {
                className: 'qa-sign-out-button',
                caption: signOutLabel,
                ariaLabel: signOutLabel,
                onClick: () => auth.killSession('Logout button clicked').andWeAreDone()
            }
        ]
    ];

    if (isSA) {
        const runAsPccOption = {
            className: 'qa-run-as-pcc-link',
            caption: `${runAsPccLabel}\u2026`,
            ariaLabel: runAsPccLabel,
            onClick: () => dialog.showModal(modalConfig)
        };

        const runAsSaOption = {
            className: 'qa-run-as-sa-link',
            caption: `${runAsSaLabel}`,
            ariaLabel: runAsSaLabel,
            onClick: () => auth.runAs()
        };

        if (onBehalfOf) {
            options.unshift([
                runAsPccOption,
                runAsSaOption
            ])
        } else {
            options.unshift([
                runAsPccOption
            ])
        }
    }

    return (
        <PopoverMenu
            caption={userId}
            popoverClassName={cssClasses(
                styles.PopoverMenu
            )}
            button={{
                textOnly: true,
                className: cssClasses(
                    'qa-user-button',
                    'user-button',
                    styles.Button
                )
            }}
            options={options}
        />
    );
}